import { JsonEditor } from 'json-edit-react';
import styled from 'styled-components';
import { cleanTree } from '../../../functions/launcherConfig/cleanTree';
import { findNodeIndex } from '../../../functions/launcherElement/findNodeIndex';
import { findParentID } from '../../../functions/launcherElement/findParentID';
import { moveNodesToParentByID } from '../../../functions/launcherElement/moveNodesToParentByID';
import { removeNodeByID } from '../../../functions/launcherElement/removeNodeByID';
import {
  LauncherElementData,
  useLauncherElementsStore,
} from '../../../hooks/stores/launcherElementsStore';
import { LauncherWindowData, useLauncherWindowStore } from '../../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import {
  launcherElementInfo,
  launcherElementTypes,
} from '../../../info/launcherElementInfo';
import { DropdownInput } from '../../common/baseInputs/DropdownInput';
import { TextInput } from '../../common/baseInputs/TextInput';
import { MenubarItemSeparator } from '../../common/DropdownMenu/MenubarItemSeparator';
import { TextButton } from '../../common/TextButton';
import { useScene } from '../../../context/sceneContext';

export const AdvancedElementInfoPanel = () => {
  const scene = useScene();
  const [launcherElementsData, setLauncherElementsData] =
    useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherWindowData, setLauncherWindowData] = useLauncherWindowStore<LauncherWindowData>();
  const [UIData, setUIData] = useUIStore<UIData>();

  const selectedElements = UIData.selectedElementIDs.map(
    (id) => launcherElementsData[id]
  );

  const setElementId = (element: LauncherElementData, newId?: string) => {
    if (!newId) return;

    let elementsData = { ...launcherElementsData };
    let windowData = { ...launcherWindowData };
    const oldId = element.id;
    const parentId = findParentID(windowData.children, oldId) || undefined;
    const index = findNodeIndex(windowData.children, oldId);

    element.id = newId;
    elementsData = {
      ...elementsData,
      [newId]: JSON.parse(JSON.stringify(element)),
    };

    const [newElementsTree, removedNode, removedIDs] = removeNodeByID({
      treeNode: windowData.children,
      id: oldId,
    });
    
    if (newElementsTree) {
      windowData.children = newElementsTree;
    }
    delete elementsData[oldId];

    windowData = {
      ...windowData,
      children: [
        ...(newElementsTree || []),
        ...cleanTree([element], (node) => node?.props?.children),
      ],
    };

    windowData.children = moveNodesToParentByID({
      tree: windowData.children,
      nodeIDs: [newId],
      parentID: parentId,
      index,
    });

    setLauncherElementsData({ ...elementsData }, true);
    setLauncherWindowData({ ...windowData });

    setUIData({
      selectedElementIDs: [newId],
      elementInEditingModeID: undefined,
    });
  };

  return (
    <Container>
      {selectedElements.length === 0 && (
        <span>Select elements to display data</span>
      )}
      {selectedElements.map((element, index) => {
        if (element === undefined) return null;
        return (
          <Block key={element.id}>
            {index > 0 && <MenubarItemSeparator />}

            <Row>
              <TextInput
                label="name"
                value={element.name}
                onChange={(newValue) => {
                  scene.updateElementData(element.id, { name: newValue }, '');
                }}
              />
            </Row>

            <Row>
              <DropdownInput
                label="type"
                value={element.type}
                options={launcherElementTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                onChange={(newValue) =>
                  scene.updateElementData(element.id, { type: newValue }, '')
                }
                canBeCleared={false}
                isSearchable={false}
              />
            </Row>

            <Row>
              <TextInput
                label="id"
                value={element.id}
                onBlur={(newValue) => {
                  setElementId(element, newValue);
                }}
              />
              <TextButton
                style={{ whiteSpace: 'nowrap' }}
                onClick={() => setElementId(element, crypto.randomUUID())}
              >
                <span
                  className="icon-inprogress"
                  style={{ fontSize: '16px' }}
                />
                Generate New
              </TextButton>
            </Row>

            <Row $unactive={!['text', 'link'].includes(element.type)}>
              <TextInput
                label="text"
                value={element.props.text}
                onChange={(newValue) => {
                  scene.updateElementData(
                    element.id,
                    { text: newValue },
                    'props'
                  );
                }}
              />
            </Row>

            <Row $unactive={element.type !== 'img'}>
              <TextInput
                label="src"
                value={element.props.src}
                onChange={(newValue) => {
                  scene.updateElementData(
                    element.id,
                    { src: newValue },
                    'props'
                  );
                }}
              />
            </Row>

            <Row $unactive={element.type !== 'link'}>
              <TextInput
                label="href"
                value={element.props.href}
                onChange={(newValue) => {
                  scene.updateElementData(
                    element.id,
                    { href: newValue },
                    'props'
                  );
                }}
              />
            </Row>

            <Row $unactive={element.type !== 'variableText'}>
              <DropdownInput
                label="variable"
                value={element.props.variable}
                options={Object.keys(
                  launcherElementInfo?.variableText?.variables || []
                ).map((type) => ({
                  label: type,
                  value: type,
                }))}
                onChange={(newValue) =>
                  scene.updateElementData(element.id, { type: newValue }, '')
                }
                canBeCleared={false}
                isSearchable={false}
              />
            </Row>

            {/* TODO: add back targetVariableInput functionality
            <Row $unactive={element.type !== 'targetVariableInput'}>
              <DropdownInput
                label="target"
                value={element.props.targetVariable}
                options={Object.keys(
                  launcherElementInfo?.targetVariableInput?.targetVariables || []
                ).map((type) => ({
                  label: type,
                  value: type,
                }))}
                onChange={(newValue) =>
                  scene.updateElementData(element.id, { targetVariable: newValue }, 'props')
                }
                canBeCleared={false}
                isSearchable={false}
              />
            </Row>
            */}

            <Row $unactive={element.type !== 'button'}>
              <Label>commands</Label>
              <TextButton
                onClick={() => {
                  const newCommands = [
                    ...(element?.props?.commands || []),
                    { type: 'startAppCommand' },
                  ];
                  scene.updateElementData(
                    element.id,
                    { commands: newCommands },
                    'props'
                  );
                }}
              >
                <span className="icon-plus" />
                Add Command
              </TextButton>
            </Row>

            {element?.props?.commands?.map((command, index) => (
              <Block
                key={element.id + '-command-' + index}
                style={{ paddingLeft: '70px' }}
              >
                {index > 0 && <MenubarItemSeparator />}

                <Row>
                  <DropdownInput
                    value={command.type}
                    options={Object.keys(
                      launcherElementInfo?.button?.commands || []
                    ).map((type) => ({
                      label: type,
                      value: type,
                    }))}
                    onChange={(newValue) => {
                      const newCommands = [...(element?.props?.commands || [])];
                      newCommands[index] = {
                        ...newCommands[index],
                        type: newValue as string,
                      };
                      scene.updateElementData(
                        element.id,
                        { commands: newCommands },
                        'props'
                      );
                    }}
                    canBeCleared={false}
                    isSearchable={false}
                  />
                  <TextButton
                    onClick={() => {
                      const newCommands = [
                        ...(element?.props?.commands || []),
                      ].filter((_, i) => i !== index);
                      scene.updateElementData(
                        element.id,
                        { commands: newCommands },
                        'props'
                      );
                    }}
                  >
                    Remove
                  </TextButton>
                </Row>

                <Row $unactive={command.type !== 'startAppCommand'}>
                  <TextInput
                    label="args"
                    value={
                      element?.props?.commands?.[index]
                        .appProcessExtraArgsAsText
                    }
                    onChange={(newValue) => {
                      const newCommands = [...(element?.props?.commands || [])];
                      newCommands[index] = {
                        ...newCommands[index],
                        appProcessExtraArgsAsText: newValue,
                      };
                      scene.updateElementData(
                        element.id,
                        { commands: newCommands },
                        'props'
                      );
                    }}
                  />
                </Row>
                <Row $unactive={command.type !== 'openExternalLinkCommand'}>
                  <TextInput
                    label="linkUrl"
                    value={element?.props?.commands?.[index].linkUrl}
                    onChange={(newValue) => {
                      const newCommands = [...(element?.props?.commands || [])];
                      newCommands[index] = {
                        ...newCommands[index],
                        linkUrl: newValue,
                      };
                      scene.updateElementData(
                        element.id,
                        { commands: newCommands },
                        'props'
                      );
                    }}
                  />
                </Row>
                <Row
                  $unactive={
                    ![
                      'openPopupCommand',
                      'closePopupCommand',
                      'togglePopupCommand',
                    ].includes(command.type)
                  }
                >
                  <TextInput
                    label="popupId"
                    value={element?.props?.commands?.[index].popupId}
                    onChange={(newValue) => {
                      const newCommands = [...(element?.props?.commands || [])];
                      newCommands[index] = {
                        ...newCommands[index],
                        popupId: newValue,
                      };
                      scene.updateElementData(
                        element.id,
                        { commands: newCommands },
                        'props'
                      );
                    }}
                  />
                </Row>
              </Block>
            ))}

            <Row>
              <Label>style</Label>
            </Row>
            <Block>
              <JsonEditor
                data={ element.props.class }
                onUpdate={ ({ newData }) => {
                  scene.updateElementData(element.id, { class: newData }, 'props');
                }}
                defaultValue={''}
                rootName=''

                theme={[ 'githubDark', {
                  container: {
                    backgroundColor: 'transparent',
                    fontSize: '12px',
                  },
                }, ]}
              />
            </Block>
          </Block>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  min-width: 450px;
  padding: 5px;

  font-family: var(--font-1);
  font-size: 12px;
  color: var(--main-2);

  display: flex;
  flex-direction: column;
`;

const Label = styled.div`
  padding-left: 7px;
  padding-right: 5px;
  color: var(--main-2);
  font-family: var(--font-2);
  font-size: 13px;
  white-space: nowrap;
  display: flex
  align-items: center;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Row = styled.div<{ $unactive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;

  ${({ $unactive }) => `opacity: ${$unactive ? 0.3 : 1};`}
  transition: opacity 0.3s;
  &:hover {
    opacity: 1;
  }

  label {
    width: 50px;
  }
`;
